<template>
    <div
        class="ds-form-field"
        :class="{
            'has-error': isInvalid,
            'is-disabled': disabled,
            'theme--dark': theme == 'dark',
        }"
    >
        <div class="ds-form-field__headline" v-if="label || tooltipText">
            <label class="ds-form-field__label" v-if="label" :for="id" v-html="label"></label>
            <DsTooltip
                v-if="tooltipText"
                icon="help"
                placement="bottom"
                class="ds-form-field__tooltip"
                :is-in-storybook
            >
                <div class="ds-form-field__tooltip-content" v-html="tooltipText"></div>
            </DsTooltip>
        </div>
        <div class="ds-form-field__main">
            <div class="ds-form-field__input-container">
                <slot name="inputLeft" v-bind="inputLeftAttrs"></slot>
                <slot v-bind="inputAttrs"></slot>
                <slot name="inputRight" v-bind="inputRightAttrs"></slot>
            </div>
            <slot name="inputAfter" v-bind="inputAfterAttrs"></slot>
        </div>
        <component :is="errorTransitionComponent">
            <div
                v-if="errorMessage || supportingText"
                class="ds-form-field__footer"
                :class="{ 'ds-form-field__footer--floating': floatingError && !supportingText }"
                v-html="errorMessage || supportingText"
                role="alert"
                :aria-live="ariaLive"
                aria-atomic="true"
            ></div>
        </component>
    </div>
</template>

<script lang="ts">
import { Theme } from "../../../types/theme";
import TransitionExpandFade from "@sazka/design-system/src/transitions/transition-expand-fade.vue";
import TransitionFade from "@sazka/design-system/src/transitions/transition-fade.vue";

export interface DsFormFieldProps {
    /**
     * Unique identifier for the form field. This identifier is nessary for accessibility.
     */
    id: string;

    /**
     * Label for the form field.
     */
    label?: string;

    /**
     * Tooltip text for the form field.
     */
    tooltipText?: string;

    /**
     * Supporting text below the label.
     */
    supportingText?: string;

    /**
     * Set error state for the form field. Default is false.
     */
    isError?: boolean;

    /**
     * Error messages related to the form field.
     */
    errorMessage?: string;

    /**
     * Whether the error message should be displayed as a floating error. This does not affect shifting page layout.
     */
    floatingError?: boolean;

    /**
     * Disable the checkbox. Default is false.
     */
    disabled?: boolean;

    /**
     * The input field must be filled out. Default is false.
     */
    required?: boolean;

    /**
     * Theme of the form field. Default is light.
     */
    theme?: Theme;

    /**
     * Indicates whether the component is rendered within Storybook. This prop is needed to DsIcon component for correct rendering.
     */
    isInStorybook?: boolean;
}

export function stripFormFieldProps<T extends DsFormFieldProps>(obj: T): DsFormFieldProps {
    return {
        id: obj.id,
        label: obj.label,
        tooltipText: obj.tooltipText,
        supportingText: obj.supportingText,
        errorMessage: obj.errorMessage,
        floatingError: obj.floatingError,
        disabled: obj.disabled,
        required: obj.required,
        theme: obj.theme,
        isInStorybook: obj.isInStorybook,
    };
}
</script>

<script setup lang="ts">
import DsTooltip from "../../../tooltip/ds-tooltip.vue";
import { computed, reactive, useSlots } from "vue";

const slots = useSlots();

const props = defineProps<DsFormFieldProps>();
const isInvalid = computed(() => !!props.errorMessage || props.isError);

const inputClasses = computed(() => {
    const classesArray = ["ds-form-field__input"];

    if (slots.inputLeft) {
        classesArray.push("ds-form-field__input--left");
    }

    if (slots.inputRight) {
        classesArray.push("ds-form-field__input--right");
    }

    return classesArray;
});

const errorTransitionComponent = computed(() => {
    return props.floatingError ? TransitionFade : TransitionExpandFade;
});

const inputLeftAttrs = reactive({
    class: "ds-form-field__input-slot ds-form-field__input-slot--left",
});

const inputAttrs = reactive({
    class: inputClasses,
    ariaInvalid: isInvalid.value ? true : null,
});

const inputRightAttrs = reactive({
    class: "ds-form-field__input-slot ds-form-field__input-slot--right",
});

const inputAfterAttrs = reactive({
    class: "ds-form-field__input-after",
});

const ariaLive = computed(() => (isInvalid.value ? "assertive" : "polite"));
</script>
